import {
  applyThemeAppearance,
  resolveThemeSelection,
  DEFAULT_THEME_APPEARANCE,
  isAccentColor,
  resolveThemeColorSeed,
  type DarkNavDirection,
  type ThemeAppearance,
  useTheme,
} from "@salestrak/ui";
import React from "react";
import { useAuth } from "#app/lib/auth-context";
import { useHasStandardAppearance } from "#app/lib/hooks/use-has-standard-appearance";
import { usePersistedState } from "#app/lib/hooks/use-persisted-state";
import {
  useUpdatePreferences,
  useUserSettings,
} from "#app/lib/hooks/use-user-settings";

/**
 * The user's accent and appearance presets, applied to the document.
 *
 * Two things have to be true at once, which is why this owns state rather than
 * reading preferences straight through:
 *
 *  - **Hovering an option has to repaint before it is chosen.** The pickers
 *    preview into local state; only a real choice persists. Otherwise walking
 *    a list of presets would be one mutation per option passed over.
 *  - **A reload must not flash the shipped design.** Preferences arrive over
 *    Apollo, so the first paint would land before they do. The mirror below
 *    seeds state synchronously from localStorage, the same trick the library's
 *    ThemeProvider uses for light/dark.
 *
 * The server stays the source of truth: whenever preferences resolve, they
 * overwrite local state and re-arm the mirror.
 *
 * When the auth check finds no signed-in user, the mirror is removed and the
 * state goes back to the shipped design: it belonged to the last person on
 * this browser.
 *
 * What the document PAINTS stops at the workspace boundary. The mirror is
 * per browser, not per user, and it paints before the auth check — so
 * without a gate the sign-in screen wore whichever accent and palette the
 * last person on that machine had chosen, and a harsh custom accent broke a screen that
 * was never theirs. On a route flagged `standardAppearance` (sign-in, the
 * workspace picker and create screens) the document wears the shipped design
 * — its default accent is the neutral pair — while state keeps holding the
 * user's appearance untouched, so the in-workspace reload stays flash-free
 * and entering the workspace is where their look lands.
 */

const STORAGE_KEY = "theme_appearance";

/**
 * Reads the mirror. Anything malformed — a hand-edited entry, a value from an
 * older shape, a browser refusing storage — resolves to the shipped design
 * rather than throwing, because this runs during the first render and a throw
 * here takes the whole app down before anything paints.
 */
function readMirror(): ThemeAppearance {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_THEME_APPEARANCE;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return DEFAULT_THEME_APPEARANCE;
    }
    const { accent, theme, themeRadius, themeShadows, custom } =
      parsed as Record<string, unknown>;
    const customRecord =
      typeof custom === "object" && custom !== null
        ? (custom as Record<string, unknown>)
        : {};
    // Narrowed through named locals: a guard applied to an inline expression
    // refines that expression, not the `unknown` the value came from.
    const accentName = typeof accent === "string" ? accent : null;
    const seeds = {
      light: resolveThemeColorSeed(customRecord.light),
      dark: resolveThemeColorSeed(customRecord.dark),
    };
    return {
      accent: isAccentColor(accentName) ? accentName : null,
      // Mirrors written before the split carry ONE key for both halves; a
      // string is read as "this theme, in both modes", which is what it meant.
      theme:
        typeof theme === "string" || theme == null
          ? resolveThemeSelection(theme, theme)
          : resolveThemeSelection(
              (theme as Record<string, unknown>).light as string | null,
              (theme as Record<string, unknown>).dark as string | null,
            ),
      themeRadius: themeRadius === true,
      themeShadows: themeShadows === true,
      custom: seeds,
    };
  } catch {
    return DEFAULT_THEME_APPEARANCE;
  }
}

function writeMirror(appearance: ThemeAppearance): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(appearance));
  } catch {
    // Storage disabled or full. The preference still round-trips through the
    // API; the only thing lost is the pre-paint seed on the next reload.
  }
}

function clearMirror(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage disabled. There is no mirror to remove.
  }
}

interface ThemeAppearanceContextValue {
  /** What the page is painting, hovered preview included. */
  appearance: ThemeAppearance;
  /**
   * What the user actually chose.
   *
   * Separate from `appearance` because a preview paints without being a
   * choice: anything that describes the user's settings — a reset affordance,
   * a "customized" badge — has to read this one, or hovering an option makes
   * the interface claim a change nobody made.
   */
  committed: ThemeAppearance;
  /** Applies immediately without persisting — for a hovered option. */
  preview: (next: ThemeAppearance) => void;
  /** Applies and persists — for a chosen option or a reset. */
  commit: (next: ThemeAppearance) => void;
  /**
   * Which way the dark navigation planes travel — an UNSETTLED evaluation
   * axis with two live arrangements (see the library's `DarkNavDirection`).
   * Deliberately outside `appearance`: it is a viewer-session experiment, so
   * it mirrors to localStorage only and never rides the preference
   * round-trip, which also means the settle-echo cannot revert it.
   */
  darkNav: DarkNavDirection;
  setDarkNav: (next: DarkNavDirection) => void;
}

const ThemeAppearanceContext =
  React.createContext<ThemeAppearanceContextValue | undefined>(undefined);

export function ThemeAppearanceProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const { isAuthenticated } = useAuth().state;
  const { preferences } = useUserSettings();
  const { resolvedTheme, setTheme } = useTheme();
  const standardAppearance = useHasStandardAppearance();
  const updatePreferences = useUpdatePreferences();
  const [appearance, setAppearance] =
    React.useState<ThemeAppearance>(readMirror);
  const [committed, setCommitted] = React.useState<ThemeAppearance>(appearance);
  // Commits still round-tripping. While one is in flight, a preferences echo
  // arriving from the cache is OLDER than local state — applying it would
  // revert the change the user just made (the optimistic write, a stale
  // same-tick snapshot, or a slow response would each snap the accent or a
  // preset back moments after the pick). `settledTick` re-arms the sync
  // effect once the LAST commit settles, so the server's answer still lands
  // as the source of truth — just never on top of something newer.
  const inFlightRef = React.useRef(0);
  const [settledTick, setSettledTick] = React.useState(0);
  const { state: darkNavState, update: updateDarkNav } = usePersistedState<{
    darkNav: DarkNavDirection;
  }>("theme_dark_nav", { darkNav: "recess" });
  const setDarkNav = React.useCallback(
    (next: DarkNavDirection) => updateDarkNav({ darkNav: next }),
    [updateDarkNav],
  );

  // Null until preferences load (and while logged out), which is exactly when
  // the mirror should keep standing.
  const stored = React.useMemo<ThemeAppearance | null>(() => {
    if (!preferences) return null;
    const custom = {
      light: resolveThemeColorSeed({
        accent: preferences.themeLightAccent,
        background: preferences.themeLightBackground,
        foreground: preferences.themeLightForeground,
        contrast: preferences.themeLightContrast,
        neutralPrimary: preferences.themeLightNeutralPrimary,
      }),
      dark: resolveThemeColorSeed({
        accent: preferences.themeDarkAccent,
        background: preferences.themeDarkBackground,
        foreground: preferences.themeDarkForeground,
        contrast: preferences.themeDarkContrast,
        neutralPrimary: preferences.themeDarkNeutralPrimary,
      }),
    };
    return {
      accent: isAccentColor(preferences.themeColor)
        ? preferences.themeColor
        : null,
      // A key the catalog no longer carries falls back to the shipped design,
      // the same way a retired accent name does. Keys are never renamed, but a
      // theme can be withdrawn.
      //
      // The per-mode keys win where they exist; `themeName` is a stored
      // single-choice row from before the split and back-fills BOTH halves,
      // which is what it meant. Nobody's theme changes on the way through.
      theme: resolveThemeSelection(
        preferences.themeLightName ?? preferences.themeName,
        preferences.themeDarkName ?? preferences.themeName,
      ),
      themeRadius: preferences.themeRadius ?? false,
      themeShadows: preferences.themeShadows ?? false,
      custom,
    };
  }, [preferences]);

  React.useEffect(() => {
    if (!stored) return;
    if (inFlightRef.current > 0) return;
    setAppearance(stored);
    setCommitted(stored);
    writeMirror(stored);
  }, [stored, settledTick]);

  React.useEffect(() => {
    if (isAuthenticated !== false) return;
    clearMirror();
    setAppearance(DEFAULT_THEME_APPEARANCE);
    setCommitted(DEFAULT_THEME_APPEARANCE);
  }, [isAuthenticated]);

  // The mode follows the account too. The device's own copy (`theme_mode`)
  // paints the first frame; the saved choice then wins, so light picked on
  // one device is light on the next. A pick on this device updates the
  // cached preferences at once (optimistic), so this never undoes it.
  const storedMode = preferences?.themeMode;
  React.useEffect(() => {
    if (storedMode === "light" || storedMode === "dark" || storedMode === "system") {
      setTheme(storedMode);
    }
  }, [storedMode, setTheme]);

  // Layout, not passive: this writes the custom properties the whole page
  // paints from, so it has to land before the browser paints the commit that
  // produced it or every change shows one frame of the previous theme.
  React.useLayoutEffect(() => {
    applyThemeAppearance(
      standardAppearance ? DEFAULT_THEME_APPEARANCE : appearance,
      resolvedTheme,
      { darkNav: darkNavState.darkNav },
    );
  }, [appearance, standardAppearance, resolvedTheme, darkNavState.darkNav]);

  const commit = React.useCallback(
    (next: ThemeAppearance) => {
      setAppearance(next);
      setCommitted(next);
      writeMirror(next);
      // ONE call, not three. Each write's optimistic response is built from the
      // cached preferences, so three in the same tick would each spread the
      // same pre-change snapshot and the last would revert the other two.
      //
      // Silent: these are cosmetic and land in bursts, and a "Preference
      // updated" toast per option chosen is noise. Errors still toast.
      inFlightRef.current += 1;
      void updatePreferences(
        {
          themeColor: next.accent,
          themeLightAccent: next.custom.light?.accent ?? null,
          themeLightBackground: next.custom.light?.background ?? null,
          themeLightForeground: next.custom.light?.foreground ?? null,
          themeDarkAccent: next.custom.dark?.accent ?? null,
          themeDarkBackground: next.custom.dark?.background ?? null,
          themeDarkForeground: next.custom.dark?.foreground ?? null,
          themeLightContrast: next.custom.light?.contrast ?? null,
          themeDarkContrast: next.custom.dark?.contrast ?? null,
          themeLightNeutralPrimary: next.custom.light?.neutralPrimary ?? null,
          themeDarkNeutralPrimary: next.custom.dark?.neutralPrimary ?? null,
          themeLightName: next.theme.light,
          themeDarkName: next.theme.dark,
          // Retire the pre-split key on the FIRST write. It back-fills both
          // halves while it is set, and `null` is a real choice — Default — so
          // leaving it behind made picking Default read back as the legacy
          // theme and snap straight to it.
          themeName: null,
          themeRadius: next.themeRadius,
          themeShadows: next.themeShadows,
        },
      ).finally(() => {
        inFlightRef.current -= 1;
        if (inFlightRef.current === 0) setSettledTick((tick) => tick + 1);
      });
    },
    [updatePreferences],
  );

  const value = React.useMemo<ThemeAppearanceContextValue>(
    () => ({
      appearance,
      committed,
      preview: setAppearance,
      commit,
      darkNav: darkNavState.darkNav,
      setDarkNav,
    }),
    [appearance, committed, commit, darkNavState.darkNav, setDarkNav],
  );

  return (
    <ThemeAppearanceContext.Provider value={value}>
      {children}
    </ThemeAppearanceContext.Provider>
  );
}

export function useThemeAppearance(): ThemeAppearanceContextValue {
  const context = React.useContext(ThemeAppearanceContext);
  if (!context) {
    throw new Error(
      "useThemeAppearance must be used within a ThemeAppearanceProvider",
    );
  }
  return context;
}
